import React from 'react';
import {ForecastMapService} from '../api';
import {ForecastMapRunManifest} from '../api/types';
import useApiClient from './useApiClient';

type ForecastMapManifestStatus = 'idle' | 'loading' | 'ready' | 'empty';

type ManifestResult = {
  // Which (configId, reload) pair produced this result; a mismatch means a
  // fetch is in flight for the current key.
  key: string | null;
  status: 'ready' | 'empty';
  manifest: ForecastMapRunManifest | null;
};

/**
 * Fetches the latest completed-run manifest for a config.
 *
 * The backend answers 404 (plain-string detail) when the config has no
 * completed run yet — by far the most common non-success outcome — so every
 * fetch failure funnels into the friendly 'empty' state with a retry hook
 * rather than an error banner.
 *
 * Platform-agnostic on purpose (no DOM usage): backs the webapp screens and
 * the mobile forecast-map screen.
 */
const useForecastMapManifest = (configId: number | null) => {
  const {fetch} = useApiClient();
  const [reloadKey, setReloadKey] = React.useState(0);
  const [result, setResult] = React.useState<ManifestResult>({
    key: null,
    status: 'empty',
    manifest: null,
  });

  const key = configId === null ? null : `${configId}:${reloadKey}`;

  React.useEffect(() => {
    if (configId === null || key === null) {
      return;
    }
    let cancelled = false;
    fetch(ForecastMapService.forecastMapLatestRun(configId), {ignore404: true})
      .then((manifest) => {
        if (!cancelled) {
          setResult({key, status: 'ready', manifest});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({key, status: 'empty', manifest: null});
        }
      });
    // Drop late responses when the config changes or on unmount.
    return () => {
      cancelled = true;
    };
  }, [configId, key, fetch]);

  const reload = React.useCallback(() => setReloadKey((k) => k + 1), []);

  // Derived: no config → idle; result not landed for the current key → loading.
  const current = result.key === key && key !== null;
  const status: ForecastMapManifestStatus =
    key === null ? 'idle' : current ? result.status : 'loading';
  const manifest = current ? result.manifest : null;

  return {status, manifest, reload};
};

export default useForecastMapManifest;
